import * as SocketConstants from 'app/modules/socket/constants'
import { Observable } from 'rxjs/Observable'
import { combineEpics } from 'redux-observable'
import { SW_PATHNAME } from '@driftt/serviceworker-notifications'

import * as actions from './actions'
import * as constants from './constants'
import * as UserPropertiesSelectors from 'app/modules/user/properties/selectors'
import * as UserSelectors from 'app/modules/user/selectors'
import * as UserActions from 'app/modules/user/actions'
import * as EndUserActions from 'app/modules/endUser/actions'
import * as EndUserSelectors from 'app/modules/endUser/selectors'
import * as ConversationActions from 'app/modules/conversation/actions'
import * as ConversationSelectors from 'app/modules/conversation/selectors'
import { hasWebSocketChatNotifications } from 'app/modules/gates/selectors'
import { WEB_PUSH_SERVER_PUBLIC_KEY } from 'constants/NotificationConstants'
import { urlB64ToUint8Array } from 'utils/ByteArrayConversionHelper'

import { notifyNewMessage, notifyParticipants, notifyFollowing, notifyFollowingAccount, soundNotification, soundNotificationImmediate, displayTabIndicator, updateSubscriptionOnServer } from 'utils/NotificationHelper'
import { formatParticipantsNotification } from 'app/modules/participants/utils'
import ACCESS from 'utils/Access'

const createParticipantNotification = (state, message) => {
  const { conversationEvent, conversationId } = message
  const { type, authorUserId } = conversationEvent
  const conversationSelector = ConversationSelectors.makeConversationSelector()
  const conversation = conversationSelector(state, { conversationId })
  const endUserId = conversation.endUserId
  const endUserSelector = EndUserSelectors.makeEndUserSelector()
  const endUser = endUserSelector(state, { id: endUserId })
  const usersById = UserSelectors.orgUsersById(state).toJS()

  if (authorUserId !== 0) {
    let notification
    switch (type) {
      case 'ADDED_PARTICIPANTS':
        notification = formatParticipantsNotification(authorUserId, conversationEvent, usersById, endUser, true)
        break
      case 'REMOVED_PARTICIPANTS':
        notification = formatParticipantsNotification(authorUserId, conversationEvent, usersById, endUser, false)
        break
    }

    if (!!notification) {
      notifyParticipants(notification, message)
    }
  }
}

function browserNotification(action$, store) {
  return action$
    .ofType(SocketConstants.JOIN_CHANNEL_PENDING)
    .pluck('payload')
    .filter((payload) => payload.clusterName === SocketConstants.SocketCluster.MESSAGES)
    .filter((payload) => payload.channelName === SocketConstants.SocketChannel.USER)
    .switchMap(({ channel, leaveChannel$ }) => {
      return Observable
        .create((observer) => {
          channel.on('browser-notification', payload => {
            const state = store.getState()
            const currentUser = UserSelectors.currentUser(state)

            const { body: { message }} = payload

            if (_.get(message, 'contentType') === 'CHAT') {
              const userProperties = UserPropertiesSelectors.properties(state)
              const soundSetting = userProperties.get('humane-notifications-browser-sound', 'click')
              notifyNewMessage(message)
              if (soundSetting !== 'none') soundNotification(soundSetting)
              displayTabIndicator()
            }
            if (_.get(message, 'contentType') === 'CONVERSATION_EVENT' && _.includes(message.conversationEvent.participantIds, currentUser.id)) {
              const { conversationId } = message
              const conversationSelector = ConversationSelectors.makeConversationSelector()
              const conversation = conversationSelector(state, { conversationId })
              if (!!conversation) {
                createParticipantNotification(state, message)
              } else {
                observer.next(actions.fetchConvoForParticipantNotification({ message }))
              }
            }
          })
        })
        .takeUntil(leaveChannel$)
    })
}

function fetchConversationForNotification(action$, { getState }) {
  return action$
    .ofType(actions.fetchConvoForParticipantNotification.toString())
    .mergeMap(message => { // Wait for conversation
      const { conversationId } = message
      const conversationSelector = ConversationSelectors.makeConversationSelector()
      const conversation = conversationSelector(getState(), { conversationId })
      if (!conversation) {
        return action$.ofType(ConversationActions.receiveConversation.toString())
          .pluck('payload', 'conversation')
          .filter(conversation => conversation.id === conversationId)
          .map(() => message)
          .first()
      }

      return Observable.of(message)
    })
    .mergeMap(message => { // Wait for endUser
      const state = getState()
      const conversationSelector = ConversationSelectors.makeConversationSelector()
      const conversation = conversationSelector(state, { conversationId: message.conversationId})
      if (!conversation) {
        return Observable.empty()
      }
      const endUserSelector = EndUserSelectors.makeEndUserSelector()
      const endUser = endUserSelector(state, { id: conversation.endUserId })

      if (!endUser) {
        return action$.ofType(EndUserActions.receiveEndUser.toString())
          .pluck('payload')
          .filter(endUser => endUser.id === conversation.endUserId)
          .map(() => message)
          .first()
      }

      return Observable.of(message)
    })
    .do(message => {
      const state = getState()
      createParticipantNotification(state, message)
    })
    .mergeMap(() => Observable.empty()) // Don't dispatch any actions
}

function followBrowserNotifications(action$, store) {
  return action$
    .ofType(SocketConstants.JOIN_CHANNEL_PENDING)
    .pluck('payload')
    .filter((payload) => payload.clusterName === SocketConstants.SocketCluster.MESSAGES)
    .filter((payload) => payload.channelName === SocketConstants.SocketChannel.ORG_PRIVATE)
    .switchMap(({ channel, leaveChannel$ }) => {
      const settings = UserPropertiesSelectors.properties(store.getState())
      const hasFollowEnabled = settings.get('follow_accounts')
      const currentUserId = UserSelectors.currentUser(store.getState()).id
      return Observable
        .create(() => {
          channel.on('end-user-presence', payload => {
            if (payload.userId === currentUserId) {
              notifyFollowing(payload)
            }
          })
          if (ACCESS.hasGates('ACCOUNTS_FOLLOW') && hasFollowEnabled) {
            channel.on('account-presence', payload => {
              const isOwner = payload.account.ownerId === currentUserId
              isOwner && notifyFollowingAccount(payload)
            })
          }
        })
        .takeUntil(leaveChannel$)
    })
}


const isAbmNotificationClick = event => {
  return event.data && event.data.onClickUrl && event.data.onClickUrl.indexOf('endUserId') > -1
}

const isSoundMessage = event => event.data === 'notification'

const subscribeToMessages = () => {
  return Observable.fromEvent(navigator.serviceWorker, 'message')
}

const registerServiceWorker$ = () => { 
  const promise = navigator.serviceWorker.register(SW_PATHNAME).then(swRegistration => {
    // Checks if service worker script has been updated if the service worker is already running
    // and reloads with the new script if it has changed
    swRegistration.update()

    return swRegistration.pushManager.getSubscription().then(function(subscription) {
      if (subscription) {       
        return updateSubscriptionOnServer(subscription)
      } else {
        const applicationServerKey = urlB64ToUint8Array(WEB_PUSH_SERVER_PUBLIC_KEY)

        return swRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey
        })        
        .then(subscription => updateSubscriptionOnServer(subscription))
      }
    }).catch(console.error)
  })

  return Observable.fromPromise(promise)
}

const isServiceWorkerSupportedAndDesktopNotificationsEnabled = state => () => 'serviceWorker' in navigator && 'PushManager' in window && !hasWebSocketChatNotifications(state) && Notification.permission === 'granted'

const registerForPushNotifications = (action$, { getState }) => action$
  .ofType(UserActions.receiveCurrentUser.toString())
  .filter(isServiceWorkerSupportedAndDesktopNotificationsEnabled(getState()))
  .first()
  .mergeMap(registerServiceWorker$)
  .map(() => actions.serviceWorkerRegistered())
  .catch(() => Observable.of(actions.failedToRegisterServiceWorker()))

const subsribeOnServiceWorkerMessages = action$ => action$
  .ofType(constants.SW_REGISTRATION_SUCCESS)
  .mergeMap(() => subscribeToMessages())
  .map(r => actions.serviceWorkerMessage(r))

const playSoundOnNotificationClick = (action$, { getState }) => action$
  .ofType(constants.SERVICE_WORKER_MESSAGE)
  .pluck('payload')
  .filter(isSoundMessage)
  .do(() => {
    const userProperties = UserPropertiesSelectors.properties(getState())
    const soundSetting = userProperties.get('humane-notifications-browser-sound', 'click')
    if (soundSetting !== 'none') soundNotificationImmediate(soundSetting)
    displayTabIndicator()  
  })
  .map(() => actions.soundPlayed())
  .catch(() => Observable.of(actions.soundPlayFailed()))

const toEndUserIdAndAttributes = event => {
  const url = new URL(`${window.location.origin}${event.data.onClickUrl}`)
  return { 
    endUserId: url.searchParams.get('endUserId'),
    companyName: url.searchParams.get('companyName'),
    page: url.searchParams.get('page')
  }
}

const showModalOnAbmNotificationClick = action$ => action$
  .ofType(constants.SERVICE_WORKER_MESSAGE)
  .pluck('payload')
  .filter(isAbmNotificationClick)
  .map(toEndUserIdAndAttributes)
  .map(endUserIdAndAttributes => actions.showAbmModal(endUserIdAndAttributes))
  .catch(() => Observable.of(actions.showAbmModalFailed()))


export const epic = combineEpics(
  browserNotification,
  fetchConversationForNotification,
  followBrowserNotifications,
  registerForPushNotifications,
  subsribeOnServiceWorkerMessages,
  playSoundOnNotificationClick,
  showModalOnAbmNotificationClick,
)



// WEBPACK FOOTER //
// ./src/scripts/app/modules/notification/epic.js